import {
  getJob,
  listClientsCached,
  listRateOverridesCached,
  listSitesCached,
  listWorkTypesCached,
  putJobLocal,
  type LocalJob,
} from '../db/jobsDb.js';
import { isLineKind, showsDuration, type LineKind } from '../domain/lineKind.js';
import {
  RateResolutionError,
  resolveRate,
} from '../../../src/domain/rates.js';
import { formatPenceGBP } from '../../../src/invoicing/money.js';

export type JobDetailHooks = {
  onEdit: (job: LocalJob) => void;
  onDeleted: () => void;
  onClose: () => void;
  onOpenReceipt?: (id: string) => void;
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function durationLabel(job: LocalJob): string {
  if (job.duration_type === 'full_day') return 'Full day';
  if (job.duration_type === 'half_day') return 'Half day';
  if (job.custom_hours != null) return `${job.custom_hours} hours`;
  if (job.custom_amount != null)
    return `£${Number(job.custom_amount).toFixed(2)}`;
  return 'Custom';
}

function formatDate(iso: string): string {
  const day = iso.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (!day) return iso || '—';
  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function kindOf(lineKind: string | undefined): LineKind {
  return lineKind && isLineKind(lineKind) ? lineKind : 'day_rate';
}

export async function mountJobDetail(
  root: HTMLElement,
  hooks: JobDetailHooks,
): Promise<{
  open: (id: string) => Promise<void>;
  clear: () => void;
}> {
  async function open(id: string) {
    const job = await getJob(id);
    if (!job || job.deleted_at) {
      root.innerHTML = `<p class="meta">Job not found.</p>`;
      return;
    }

    const [clients, types, sites, overrides] = await Promise.all([
      listClientsCached(),
      listWorkTypesCached(),
      listSitesCached(),
      listRateOverridesCached(),
    ]);
    const client = clients.find((c) => c.id === job.client_id);
    const workType = types.find((t) => t.id === job.work_type_id);
    const siteNames = (job.site_ids ?? [])
      .map((sid) => sites.find((s) => s.id === sid)?.name)
      .filter(Boolean) as string[];
    const sitesText =
      siteNames.join(' · ') || job.site_reference?.trim() || '—';

    let rateText = '—';
    const kind = kindOf(workType?.line_kind);
    if (showsDuration(kind) && job.duration_type !== 'custom') {
      const overrideRow = overrides.find(
        (o) =>
          o.client_id === job.client_id && o.work_type_id === job.work_type_id,
      );
      try {
        const { amountPence } = resolveRate(
          {
            duration_type: job.duration_type,
            custom_hours: null,
            custom_amount: null,
          },
          {
            day_rate: client?.day_rate ?? '',
            half_day_rate: client?.half_day_rate ?? '',
            hourly_rate: client?.hourly_rate ?? null,
          },
          overrideRow
            ? {
                day_rate: overrideRow.day_rate,
                half_day_rate: overrideRow.half_day_rate,
                hourly_rate: overrideRow.hourly_rate,
              }
            : null,
        );
        rateText =
          amountPence > 0 ? formatPenceGBP(amountPence) : 'No rate configured';
      } catch (err) {
        rateText =
          err instanceof RateResolutionError
            ? err.message
            : 'No rate configured';
      }
    } else if (job.custom_amount != null) {
      rateText = `£${Number(job.custom_amount).toFixed(2)}`;
    } else if (job.custom_hours != null) {
      rateText = `${job.custom_hours}h (hourly)`;
    }

    const invoiced = Boolean(job.invoice_run_id);
    const invoiceLabel = job.invoice_number
      ? `#${job.invoice_number}`
      : 'invoice';

    root.innerHTML = `
      <div class="job-detail">
        ${
          invoiced
            ? `<p class="job-detail-lock" role="status">Invoiced on ${escapeHtml(invoiceLabel)} — cannot be edited</p>`
            : ''
        }
        ${
          job.failReason
            ? `<p class="job-detail-fail" role="alert">${escapeHtml(job.failReason)}</p>`
            : ''
        }
        <dl class="job-detail-dl">
          <div><dt>Date</dt><dd>${escapeHtml(formatDate(job.work_date))}</dd></div>
          <div><dt>Client</dt><dd>${escapeHtml(client?.name ?? 'Client')}</dd></div>
          <div><dt>Work type</dt><dd>${escapeHtml(workType?.short_description ?? '—')}</dd></div>
          <div><dt>Sites</dt><dd>${escapeHtml(sitesText)}</dd></div>
          <div><dt>Duration</dt><dd>${escapeHtml(durationLabel(job))}</dd></div>
          <div><dt>Rate</dt><dd>${escapeHtml(rateText)}</dd></div>
          <div><dt>Billable</dt><dd>${job.billable === false ? 'No' : 'Yes'}</dd></div>
          <div><dt>Status</dt><dd><span class="pill">${escapeHtml(job.status)}</span></dd></div>
          ${
            job.po_number
              ? `<div><dt>PO</dt><dd>${escapeHtml(job.po_number)}</dd></div>`
              : ''
          }
          ${
            job.notes
              ? `<div class="job-detail-notes"><dt>Notes</dt><dd>${escapeHtml(job.notes)}</dd></div>`
              : ''
          }
          ${
            invoiced
              ? `<div><dt>Invoice</dt><dd>${escapeHtml(invoiceLabel)}</dd></div>`
              : ''
          }
          ${
            job.recharge_receipt_id
              ? `<div><dt>Receipt</dt><dd>Recharged from a receipt</dd></div>`
              : ''
          }
        </dl>
        <div class="job-detail-actions">
          ${
            job.recharge_receipt_id
              ? `<button type="button" class="btn-secondary" data-open-receipt>View receipt</button>`
              : ''
          }
          ${
            invoiced
              ? ''
              : `<button type="button" class="btn-primary" data-edit>Edit</button>
                 <button type="button" class="btn-secondary" data-delete>Delete</button>`
          }
          <button type="button" class="btn-secondary" data-close-detail>Close</button>
        </div>
      </div>
    `;

    root.querySelector('[data-open-receipt]')?.addEventListener('click', () => {
      if (job.recharge_receipt_id) hooks.onOpenReceipt?.(job.recharge_receipt_id);
    });

    root.querySelector('[data-close-detail]')?.addEventListener('click', () => {
      hooks.onClose();
    });

    root.querySelector('[data-edit]')?.addEventListener('click', () => {
      if (invoiced) return;
      hooks.onEdit(job);
    });

    root.querySelector('[data-delete]')?.addEventListener('click', () => {
      void (async () => {
        if (invoiced) return;
        const ok = window.confirm(
          'Delete this job? It stays on this phone until sync removes it from the server.',
        );
        if (!ok) return;
        const current = await getJob(id);
        if (!current || current.invoice_run_id) return;
        const now = new Date().toISOString();
        await putJobLocal({
          ...current,
          deleted_at: now,
          pendingOp: 'delete',
          syncState: 'pending',
          failReason: null,
          updatedAt: now,
        });
        hooks.onDeleted();
      })();
    });
  }

  function clear() {
    root.innerHTML = '';
  }

  return { open, clear };
}
